import { GeographyRegion, SuggestedGeography } from '@api';

export type RegionState = 'none' | 'partial' | 'all';

export const getRegionState = (
  region: GeographyRegion,
  selected: Set<string>
): RegionState => {
  if (region.countries.length === 0) {
    return 'none';
  }

  const hits = region.countries.filter(country =>
    selected.has(country.code)
  ).length;

  if (hits === 0) {
    return 'none';
  }

  if (hits === region.countries.length) {
    return 'all';
  }

  return 'partial';
};

export type FollowedCountry = {
  code: string;
  name: string;
};

export type FollowedGeography =
  | {
      kind: 'region';
      code: string;
      name: string;
      countryCodes: string[];
    }
  | {
      kind: 'country';
      code: string;
      name: string;
    };

export const groupFollowedCountriesByRegion = (
  regions: GeographyRegion[],
  selected: Set<string>
): FollowedGeography[] => {
  const followedGeographies: FollowedGeography[] = [];
  const absorbed = new Set<string>();

  for (const region of regions) {
    if (region.countries.length === 0) {
      continue;
    }

    const allSelected = region.countries.every(country =>
      selected.has(country.code)
    );

    if (!allSelected) {
      continue;
    }

    followedGeographies.push({
      kind: 'region',
      code: region.code,
      name: region.name,
      countryCodes: region.countries.map(country => country.code),
    });
    region.countries.forEach(country => absorbed.add(country.code));
  }

  const codeToName = new Map<string, string>();

  for (const region of regions) {
    for (const country of region.countries) {
      if (!codeToName.has(country.code)) {
        codeToName.set(country.code, country.name);
      }
    }
  }

  for (const code of selected) {
    if (absorbed.has(code)) {
      continue;
    }

    followedGeographies.push({
      kind: 'country',
      code,
      name: codeToName.get(code) ?? code,
    });
  }

  return followedGeographies;
};

export const getFollowedGeographyNames = (
  regions: GeographyRegion[],
  selected: Set<string>
) => groupFollowedCountriesByRegion(regions, selected).map(({ name }) => name);

export const mapSelectedCountries = (
  regions: GeographyRegion[],
  selected: Set<string>
) => {
  const codeToName = new Map(
    regions.flatMap(region =>
      region.countries.map(country => [country.code, country.name] as const)
    )
  );

  return [...selected].map<FollowedCountry>(code => ({
    code,
    name: codeToName.get(code) ?? code,
  }));
};

export const getSuggestedCountryCodes = (
  suggestions: SuggestedGeography[],
  regions: GeographyRegion[]
) => {
  const countriesByRegion = new Map(
    regions.map(region => [region.code, region.countries])
  );
  const countryCodes = new Set<string>();

  for (const suggestion of suggestions) {
    if (suggestion.type === 'country') {
      countryCodes.add(suggestion.code);
      continue;
    }

    countriesByRegion
      .get(suggestion.code)
      ?.forEach(country => countryCodes.add(country.code));
  }

  return countryCodes;
};
